import { useEffect, useState } from "react";
import type { ResolvedGuardianInviteDto } from "@madamuh/domain";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError } from "../../shared/api/client";
import { consumeGuardianInvite, resolveGuardianInvite } from "../../shared/api/guardian";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import {
  clearFlowTransientState,
  readFlowString,
  readFlowToken,
  rememberFlowReturnTo,
  rememberFlowTenantSlug,
  rememberFlowToken,
  writeFlowString
} from "../../shared/session/flowStorage";
import { formatTenantDateTime } from "../../shared/lib/tenantDateTime";
import { describeRelationType, describeStatus } from "../../shared/lib/uiLabels";
import { useSessionBootstrap } from "../../shared/session/SessionBootstrapProvider";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { PageScaffold } from "../../shared/ui/PageScaffold";

type ResolveState = "idle" | "loading" | "ready" | "error";
type ConsumeState = "idle" | "submitting" | "success" | "error";

function formatGuardianInvitePageError(error: unknown, fallbackMessage: string): string {
  if (error instanceof ApiError) {
    if (error.code === "INVITE_NOT_FOUND") {
      return "招待が見つかりません。招待URLまたは招待コードを確認してください。";
    }

    if (error.code === "INVITE_EXPIRED") {
      return "招待の有効期限が切れています。管理者に再発行を依頼してください。";
    }

    if (error.code === "INVITE_REVOKED") {
      return "この招待は無効化されています。管理者に確認してください。";
    }

    if (error.code === "INVITE_ALREADY_USED") {
      return "この招待は使用上限に達しています。管理者に新しい招待を依頼してください。";
    }

    if (error.code === "INVITE_TARGET_MISSING") {
      return "招待対象の生徒情報を確認できません。管理者に確認してください。";
    }

    if (error.code === "TENANT_SESSION_MISMATCH" || error.code === "TENANT_NOT_FOUND") {
      return "現在ログイン中の tenant ではこの招待を利用できません。招待URLの tenant を確認してください。";
    }

    if (error.code === "INVITE_UNAVAILABLE") {
      return "この招待は現在利用できません。有効期限切れ、無効化済み、または使用上限に達している可能性があります。";
    }
  }

  return getProtectedApiErrorMessage(error, fallbackMessage);
}

function getGuardianInviteStatusMessage(invite: ResolvedGuardianInviteDto): string | null {
  if (invite.alreadyConsumedByCurrentUser) {
    return "このアカウントではすでに同じ招待を消費済みです。再実行しても紐付けは増えません。";
  }

  if (invite.status === "expired") {
    return "この招待は期限切れです。管理者に再発行を依頼してください。";
  }

  if (invite.status === "revoked") {
    return "この招待は無効化されています。管理者に確認してください。";
  }

  if (invite.status === "consumed") {
    return "この招待は使用上限に達しています。管理者に新しい招待を依頼してください。";
  }

  return null;
}

export function LinkStudentConfirmPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const [searchParams] = useSearchParams();
  const { bootstrapSession } = useSessionBootstrap();
  const { routeStatus, sessionStatus, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [resolvedInvite, setResolvedInvite] = useState<ResolvedGuardianInviteDto | null>(null);
  const [resolveState, setResolveState] = useState<ResolveState>("idle");
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [consumeState, setConsumeState] = useState<ConsumeState>("idle");
  const [consumeMessage, setConsumeMessage] = useState<string | null>(null);
  const [lastResolvedKey, setLastResolvedKey] = useState<string | null>(null);

  const inviteToken = searchParams.get("inviteToken")?.trim() || readFlowToken("guardianInvite", tenantSlug) || "";
  const manualCode = searchParams.get("manualCode")?.trim() || readFlowString("guardianInvite", "manualCode") || "";
  const resolutionKey = inviteToken ? `token:${inviteToken}` : manualCode ? `code:${manualCode}` : null;
  const currentConfirmPath = inviteToken
    ? `/t/${tenantSlug}/link-student/confirm?inviteToken=${encodeURIComponent(inviteToken)}`
    : manualCode
      ? `/t/${tenantSlug}/link-student/confirm?manualCode=${encodeURIComponent(manualCode)}`
      : `/t/${tenantSlug}/link-student/confirm`;

  useEffect(() => {
    setResolvedInvite(null);
    setResolveError(null);
    setResolveState("idle");
    setConsumeState("idle");
    setConsumeMessage(null);
    setLastResolvedKey(null);
  }, [resolutionKey]);

  useEffect(() => {
    if (!resolutionKey) {
      return;
    }

    rememberFlowTenantSlug("guardianInvite", tenantSlug);
    rememberFlowReturnTo("guardianInvite", currentConfirmPath);

    if (inviteToken) {
      rememberFlowToken("guardianInvite", inviteToken);
    }

    if (manualCode) {
      writeFlowString("guardianInvite", "manualCode", manualCode);
    }
  }, [currentConfirmPath, inviteToken, manualCode, resolutionKey, tenantSlug]);

  useEffect(() => {
    if (!isSessionReady || !resolutionKey || resolutionKey === lastResolvedKey) {
      return;
    }

    let cancelled = false;

    async function runResolve() {
      setResolveState("loading");
      setResolveError(null);

      try {
        const nextInvite = await resolveGuardianInvite(tenantSlug, {
          inviteToken: inviteToken || null,
          manualCode: manualCode || null
        });

        if (cancelled) {
          return;
        }

        setResolvedInvite(nextInvite);
        setResolveState("ready");
        setLastResolvedKey(resolutionKey);
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setResolvedInvite(null);
        setResolveState("error");
        if (error instanceof ApiError && ["INVITE_NOT_FOUND", "INVITE_EXPIRED", "INVITE_REVOKED", "INVITE_ALREADY_USED"].includes(error.code ?? "")) {
          clearFlowTransientState("guardianInvite");
        }
        setResolveError(formatGuardianInvitePageError(error, "招待内容の取得に失敗しました"));
      }
    }

    void runResolve();

    return () => {
      cancelled = true;
    };
  }, [inviteToken, isSessionReady, lastResolvedKey, manualCode, navigate, resolutionKey, tenantSlug]);

  async function handleConsumeInvite() {
    if (!resolvedInvite) {
      return;
    }

    setConsumeState("submitting");
    setConsumeMessage(null);

    try {
      const response = await consumeGuardianInvite(tenantSlug, {
        inviteId: resolvedInvite.inviteId
      });
      const nextSession = await bootstrapSession(tenantSlug);
      clearFlowTransientState("guardianInvite");

      setConsumeState("success");
      setConsumeMessage(
        response.alreadyConsumed
          ? "この招待はすでにこのアカウントで消費済みです。ホームへ戻します。"
          : "招待を消費しました。ホームへ移動します。"
      );

      if (nextSession) {
        navigate(nextSession.entryPath, { replace: true });
      }
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setConsumeState("error");
      if (error instanceof ApiError && ["INVITE_NOT_FOUND", "INVITE_EXPIRED", "INVITE_REVOKED", "INVITE_ALREADY_USED"].includes(error.code ?? "")) {
        clearFlowTransientState("guardianInvite");
      }
      setConsumeMessage(formatGuardianInvitePageError(error, "招待消費に失敗しました"));
    }
  }

  return (
    <PageScaffold
      title="保護者招待の確認"
      description="招待URL / 招待コードの解決結果を確認し、保護者紐付けを消費する画面です。"
      links={[
        { to: "/t/:tenantSlug/link-student", label: "入力へ戻る" },
        { to: "/t/:tenantSlug/link-student/request", label: "申請へ進む" }
      ]}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {!resolutionKey ? (
          <p>
            招待URL token か招待コードがありません。<Link to={`/t/${tenantSlug}/link-student`}>入力画面</Link>
            からやり直してください。
          </p>
        ) : null}

        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}

        {isSessionReady && resolutionKey ? (
          <>
            {resolveState === "loading" ? <p>招待内容を確認しています。</p> : null}
            {resolveState === "error" ? (
              <p>
                招待内容の取得に失敗しました。
                {resolveError ? ` ${resolveError}` : ""}
              </p>
            ) : null}

            {resolvedInvite ? (
              <div style={{ display: "grid", gap: 12 }}>
                <div>
                  <strong>招待範囲</strong>: {describeStatus(resolvedInvite.scopeType)}
                </div>
                <div>
                  <strong>続柄</strong>: {describeRelationType(resolvedInvite.relationTypeDefault)}
                </div>
                <div>
                  <strong>状態</strong>: {describeStatus(resolvedInvite.status)}
                </div>
                <div>
                  <strong>残り利用回数</strong>: {resolvedInvite.remainingUseCount} / {resolvedInvite.maxUseCount}
                </div>
                <div>
                  <strong>有効期限</strong>: {formatTenantDateTime(resolvedInvite.expiresAt)}
                </div>
                <div>
                  <strong>対象生徒</strong>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                    {resolvedInvite.students.map((student) => (
                      <li key={student.studentId}>
                        {student.displayName} ({student.studentId})
                      </li>
                    ))}
                  </ul>
                </div>
                {resolvedInvite.alreadyConsumedByCurrentUser ? (
                  <p style={{ margin: 0 }}>{getGuardianInviteStatusMessage(resolvedInvite)}</p>
                ) : null}
                {!resolvedInvite.alreadyConsumedByCurrentUser && resolvedInvite.status !== "active" ? (
                  <p style={{ margin: 0 }}>{getGuardianInviteStatusMessage(resolvedInvite)}</p>
                ) : null}

                <button
                  type="button"
                  onClick={() => void handleConsumeInvite()}
                  disabled={
                    consumeState === "submitting" ||
                    (resolvedInvite.status !== "active" && !resolvedInvite.alreadyConsumedByCurrentUser)
                  }
                >
                  {consumeState === "submitting" ? "処理中..." : "この招待を消費する"}
                </button>

                {consumeMessage ? <p style={{ margin: 0 }}>{consumeMessage}</p> : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </PageScaffold>
  );
}
